import React, { useState, useEffect } from 'react';
import { ChevronDown, User, Menu, X, Bell, ArrowRight } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavbarProps {
  onLoginClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleMouseEnter = (menu: string) => {
    if (window.innerWidth >= 768) { // Only on desktop
      setActiveDropdown(menu);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth >= 768) { // Only on desktop
      setActiveDropdown(null);
    }
  };

  const toggleMobileDropdown = (menu: string) => {
    if (window.innerWidth < 768) { // Only on mobile
      setActiveDropdown(activeDropdown === menu ? null : menu);
    }
  };

  const handleLoginClick = () => {
    navigate('/uye-girisi');
  };

  return (
    <nav className="sticky top-0 z-50 bg-[#F7BA45] shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 text-xl md:text-2xl font-medium text-slate-900 hover:opacity-90 transition-opacity"
          >
            <div className="bg-white p-1.5 md:p-2 rounded-lg md:rounded-xl flex items-center gap-1">
              <svg width="48" height="24" viewBox="0 0 48 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.4983 0.000244141H19.5073L6.52164 11.7677L20.0199 24.0002H13.4983L0 11.7677L13.4983 0.000244141Z" className="fill-[#F7BA45]" />
                <path d="M25.8345 0H41.3835C41.3835 0 48.1352 2.44539 47.9983 8.00044C47.8614 13.5555 41.3835 16.0584 41.3835 16.0584H30.3225V23.9993H25.8345V11.9394C25.8345 11.9394 35.583 12.9243 39.9779 11.9394C44.3728 10.9545 43.4146 7.99988 43.4146 7.99988C43.3147 7.14266 42.934 6.33225 42.3216 5.67274C41.7091 5.01322 40.8929 4.53488 39.9779 4.29928C36.7047 3.37411 30.3219 4.29928 30.3219 4.29928L25.8345 0Z" className="fill-[#F7BA45]" />
              </svg>
            </div>
            <span className="hidden xs:block">KREDİPARK</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('kredi')}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl transition-colors ${
                activeDropdown === 'kredi' 
                  ? 'bg-white/20 text-slate-900' 
                  : 'text-slate-900 hover:bg-white/10'
              }`}>
                <span>Kredi</span>
                <ChevronDown 
                  size={16} 
                  className={`transition-transform duration-300 ${
                    activeDropdown === 'kredi' ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              <div className={`absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-lg py-3 border border-slate-200 backdrop-blur-sm transition-all duration-300 ${
                activeDropdown === 'kredi' 
                  ? 'opacity-100 visible translate-y-0' 
                  : 'opacity-0 invisible -translate-y-2'
              }`}>
                <Link to="/ihtiyac-kredisi" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  İhtiyaç Kredisi
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <Link to="/konut-kredisi" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  Konut Kredisi
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <Link to="/tasit-kredisi" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  Taşıt Kredisi
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <Link to="/kobi-kredisi" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  Kobi Kredisi
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </div>
            </div>

            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('kartlar')}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl transition-colors ${
                activeDropdown === 'kartlar' 
                  ? 'bg-white/20 text-slate-900' 
                  : 'text-slate-900 hover:bg-white/10'
              }`}>
                <span>Kredi Kartları</span>
                <ChevronDown 
                  size={16} 
                  className={`transition-transform duration-300 ${
                    activeDropdown === 'kartlar' ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              <div className={`absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-lg py-3 border border-slate-200 transition-all duration-300 ${
                activeDropdown === 'kartlar' 
                  ? 'opacity-100 visible translate-y-0' 
                  : 'opacity-0 invisible -translate-y-2'
              }`}>
                <Link to="/kredi-kartlari" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  Bireysel Kredi Kartları
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <Link to="/kredi-kartlari?type=ticari" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  Ticari Kredi Kartları
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </div>
            </div>

            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('mevduat')}
              onMouseLeave={handleMouseLeave}
            >
              <button className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl transition-colors ${
                activeDropdown === 'mevduat' 
                  ? 'bg-white/20 text-slate-900' 
                  : 'text-slate-900 hover:bg-white/10'
              }`}>
                <span>Mevduat</span>
                <ChevronDown 
                  size={16} 
                  className={`transition-transform duration-300 ${
                    activeDropdown === 'mevduat' ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              <div className={`absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-lg py-3 border border-slate-200 transition-all duration-300 ${
                activeDropdown === 'mevduat' 
                  ? 'opacity-100 visible translate-y-0' 
                  : 'opacity-0 invisible -translate-y-2'
              }`}>
                <Link to="/mevduat" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  TL Mevduat
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
                <Link to="/mevduat" className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                  Döviz Mevduat
                  <ArrowRight size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              </div>
            </div>

            <Link 
              to="/uzaktan-musteri-edinimi" 
              className="px-4 py-2.5 rounded-xl text-slate-900 hover:bg-white/10 transition-colors"
            >
              Banka Müşterisi Olun
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Notifications */}
            <Link 
              to="/bildirimler"
              className="relative p-2 md:p-2.5 rounded-lg md:rounded-xl text-slate-900 hover:bg-white/10 transition-colors md:flex hidden"
            >
              <Bell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </Link>

            {/* User Menu - Desktop only */}
            <button 
              onClick={handleLoginClick}
              className="hidden md:flex items-center gap-2 bg-slate-900 hover:bg-slate-800 px-4 py-2.5 rounded-xl transition-colors text-white"
            >
              <User size={20} />
              <span>Giriş Yap</span>
            </button>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 text-slate-900 hover:bg-white/10 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 md:hidden ${
          isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`fixed inset-y-0 right-0 w-[280px] bg-white shadow-xl transform transition-transform duration-300 ease-in-out md:hidden ${
        isMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200">
            <span className="text-lg font-medium text-slate-900">Menü</span>
            <button 
              onClick={() => setIsMenuOpen(false)}
              className="p-2 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Mobile Menu Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-6">
              {/* Kredi Section */}
              <div className="space-y-2">
                <button
                  onClick={() => toggleMobileDropdown('kredi')}
                  className="flex items-center justify-between w-full p-2 text-left text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <span className="font-medium">Kredi</span>
                  <ChevronDown 
                    size={18} 
                    className={`transition-transform duration-200 ${
                      activeDropdown === 'kredi' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`space-y-1 pl-4 transition-all duration-200 ${
                  activeDropdown === 'kredi' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                  <Link 
                    to="/ihtiyac-kredisi"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    İhtiyaç Kredisi
                  </Link>
                  <Link 
                    to="/konut-kredisi"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Konut Kredisi
                  </Link>
                  <Link 
                    to="/tasit-kredisi"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Taşıt Kredisi
                  </Link>
                  <Link 
                    to="/kobi-kredisi"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Kobi Kredisi
                  </Link>
                </div>
              </div>

              {/* Kredi Kartları Section */}
              <div className="space-y-2">
                <button
                  onClick={() => toggleMobileDropdown('kartlar')}
                  className="flex items-center justify-between w-full p-2 text-left text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <span className="font-medium">Kredi Kartları</span>
                  <ChevronDown 
                    size={18} 
                    className={`transition-transform duration-200 ${
                      activeDropdown === 'kartlar' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`space-y-1 pl-4 transition-all duration-200 ${
                  activeDropdown === 'kartlar' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                  <Link 
                    to="/kredi-kartlari"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Bireysel Kredi Kartları
                  </Link>
                  <Link 
                    to="/kredi-kartlari?type=ticari"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Ticari Kredi Kartları
                  </Link>
                </div>
              </div>

              {/* Mevduat Section */}
              <div className="space-y-2">
                <button
                  onClick={() => toggleMobileDropdown('mevduat')}
                  className="flex items-center justify-between w-full p-2 text-left text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  <span className="font-medium">Mevduat</span>
                  <ChevronDown 
                    size={18} 
                    className={`transition-transform duration-200 ${
                      activeDropdown === 'mevduat' ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div className={`space-y-1 pl-4 transition-all duration-200 ${
                  activeDropdown === 'mevduat' ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                }`}>
                  <Link 
                    to="/mevduat"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    TL Mevduat
                  </Link>
                  <Link 
                    to="/doviz-mevduat"
                    className="block p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    Döviz Mevduat
                  </Link>
                </div>
              </div>

              <Link 
                to="/uzaktan-musteri-edinimi"
                className="block p-2 text-slate-900 font-medium hover:bg-slate-50 rounded-lg transition-colors"
              >
                Banka Müşterisi Olun
              </Link>
            </div>
          </div>

          {/* Mobile Menu Footer */}
          <div className="p-4 border-t border-slate-200">
            <button 
              onClick={handleLoginClick}
              className="w-full bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-4 py-3 rounded-xl transition-colors flex items-center justify-center gap-2 font-medium"
            >
              <User size={20} />
              <span>Giriş Yap</span>
              <ArrowRight size={18} className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;